import { useEffect, useState } from 'react';
import type { ThemeSpec } from '../themes';

/** 版本号：守则内容更新时递增，所有用户需要重新确认 */
// v2：新增「禁止自动化脚本」条款——内容变更时 bump 版本，所有用户需重新确认
export const GUIDELINES_VERSION = 'bb_guidelines_v2';

export function shouldShowGuidelines(): boolean {
  try {
    return localStorage.getItem(GUIDELINES_VERSION) !== '1';
  } catch {
    return false; // 隐私模式等读不了 localStorage 时不阻断画画
  }
}

/**
 * 小黑板涂鸦守则 —— 首次进入必须主动确认。
 * 不可点遮罩/ESC 关闭，只有「我已阅读并遵守」一个出口。
 */
export function GuidelinesDialog({ theme, onAgree }: { theme: ThemeSpec; onAgree: () => void }) {
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    // 确认后给一个短淡出，避免板子突然弹出来
    if (leaving) {
      const t = setTimeout(onAgree, 180);
      return () => clearTimeout(t);
    }
  }, [leaving, onAgree]);

  function agree() {
    try {
      localStorage.setItem(GUIDELINES_VERSION, '1');
    } catch { /* private mode */ }
    setLeaving(true);
  }

  const surfaceStyle: React.CSSProperties = {
    background: theme.surface,
    color: theme.surfaceText,
    border: `1px solid ${theme.surfaceBorder}`,
    boxShadow: theme.surfaceShadow,
  };

  const rules = [
    {
      icon: '✋',
      title: '尊重他人作品',
      body: '不覆盖、不涂抹、不破坏别人画的内容。',
    },
    {
      icon: '⚖️',
      title: '遵守法律法规',
      body: '不书写色情、暴力、辱骂、谣言等任何违法违规内容。',
    },
    {
      icon: '🤖',
      title: '禁止自动化脚本',
      body: '不得使用脚本、外挂等自动化工具绘画或刷屏，系统自动检测，违者封禁。',
    },
    {
      icon: '🚨',
      title: '违规必究',
      body: '违规内容将留存证据并依法向有关部门反映，账号将被封禁。',
    },
  ];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="小黑板涂鸦守则"
      className={`fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/55 transition-opacity duration-200 cursor-default ${leaving ? 'opacity-0' : 'opacity-100'}`}
      style={{ backdropFilter: 'blur(2px)' }}
    >
      <div
        className="bb-pop w-full max-w-[400px] rounded-2xl p-5 sm:p-6"
        style={surfaceStyle}
      >
        <div className="flex items-center gap-2.5 mb-1">
          <span className="text-xl" aria-hidden>黑板</span>
          <h2 className="text-[17px] font-semibold tracking-wide" style={{ fontFamily: theme.fontFamily }}>
            小黑板涂鸦守则
          </h2>
        </div>
        <p className="text-[12px] mb-4" style={{ color: theme.surfaceTextMuted }}>
          开始涂鸦前，请先确认这三条：
        </p>
        <ul className="flex flex-col gap-3 mb-5">
          {rules.map((r) => (
            <li key={r.title} className="flex gap-3 items-start">
              <span
                aria-hidden
                className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-[15px]"
                style={{ background: 'rgba(109,76,140,0.25)' }}
              >
                {r.icon}
              </span>
              <div className="min-w-0">
                <div className="text-[13.5px] font-semibold leading-5">{r.title}</div>
                <div className="text-[12.5px] leading-[1.55] mt-0.5" style={{ color: theme.surfaceTextMuted }}>
                  {r.body}
                </div>
              </div>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={agree}
          autoFocus
          className="w-full h-11 rounded-xl text-[14px] font-semibold text-white transition-transform active:scale-[0.98]"
          style={{ background: '#6D4C8C', boxShadow: '0 6px 18px -6px rgba(109,76,140,0.6)' }}
        >
          我已阅读并遵守
        </button>
        <p className="mt-3 text-center text-[11px]" style={{ color: theme.surfaceTextMuted, opacity: 0.7 }}>
          依据《网络信息内容生态治理规定》等法规
        </p>
      </div>
    </div>
  );
}
